import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { logoutSession } from '../services/authSession';

export type AppUser = {
  id: number;
  email: string;
  name: string;
  role: string;
  billingDueDay?: number | null;
};

interface AuthContextType {
  user: User | null;
  appUser: AppUser | null;
  loading: boolean;
  idToken: string | null;
  setAppUser: (user: AppUser | null) => void;
  logout: () => Promise<void>;
}

const APP_USER_KEY = 'app_user';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readStoredUser(): AppUser | null {
  try {
    const raw = localStorage.getItem(APP_USER_KEY);
    return raw ? (JSON.parse(raw) as AppUser) : null;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [appUser, setAppUserState] = useState<AppUser | null>(readStoredUser());
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string | null>(null);

  const setAppUser = (next: AppUser | null) => {
    setAppUserState(next);
    if (next) {
      localStorage.setItem(APP_USER_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(APP_USER_KEY);
    }
  };

  const logout = async () => {
    if (localStorage.getItem('jwt_token')) {
      await logoutSession();
    }
    localStorage.removeItem('jwt_token');
    setAppUser(null);
    if (auth) {
      await signOut(auth);
    }
    window.location.href = '/login';
  };

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (nextUser) {
        const token = await nextUser.getIdToken();
        setIdToken(token);
      } else {
        setIdToken(null);
        if (!localStorage.getItem('jwt_token')) {
          setAppUser(null);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, appUser, loading, idToken, setAppUser, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
